import { lazy, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { HeroSection } from '../features/home/components/HeroSection'
import { useDocumentMeta } from '../hooks/useDocumentMeta'
import { routes } from '../lib/navigation'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE HOMEPAGE, REBUILT 22 SEP TO THE CLIENT'S OWN REFERENCE DESIGN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `Docs/From Client/Nettside - Example.pdf` is the client's example of how the
 * marketplace website should look, and the developer confirmed it is
 * specifically THIS page they want to look like it. Its section order and its
 * wording are followed closely, because matching their voice is the point.
 * The sections themselves are `features/home/components/HomeSections.tsx`; the
 * order and the reasoning are in its header and in `features/home/data.ts`.
 *
 * ─── WHY THIS FILE IS SPLIT IN TWO ─────────────────────────────────────────
 *
 * `/` is the entry route, so it is not code-split, and the site had 11.1 kB of
 * gzipped headroom against Docs/03's 180 kB budget. Sixteen new sections do not
 * fit in that. Cutting the client's design to make them fit would have been
 * solving the wrong problem, so the page is split at the fold instead: the hero
 * is eager and everything below it is one lazy chunk, carrying its own strings
 * (`locales/home/`) with it.
 *
 * There is no `Suspense` fallback UI, and that is deliberate. The boundary
 * resolves in one module fetch that starts the moment the page mounts, while
 * the hero — a full screen of content — is already painted. A spinner or a
 * skeleton block under the fold would be a flash of loading state for something
 * nobody has scrolled to yet.
 *
 * SEO is unaffected. This site is client-rendered on every route (Netlify
 * serves the same `index.html` shell for `/*`), so a crawler was always
 * executing JavaScript to see any of it; `useDocumentMeta` runs here, in the
 * eager half.
 *
 * ─── TWO RULES THIS PAGE HAS ALREADY BROKEN ONCE ───────────────────────────
 *
 * ⚠️ NO NUMBER RENDERS HERE UNLESS `public.platform_stats()` RETURNED IT. This
 * page once printed 12 480 businesses, 38 200 requests, 91 500 reviews and a
 * 4.7 average out of `lib/mockData.ts`, live on the client's marketing site,
 * when the real figures were 10, 42, 39 and 4.64. The client's reference PDF
 * invents numbers too — its category tiles read "12 businesses / 38 businesses
 * / 9 businesses" — and those are NOT copied. Where a real figure would make a
 * section look empty, the section carries no figure: `CategoryGrid` and the
 * FAQ's "how many offers" answer both say so in place.
 *
 * ⚠️ NEVER TYPE THE PRODUCT NAME. It is being renamed (`Docs/26-RENAME.md`).
 * The catalogues carry `{{product}}`, `{{productUpper}}`, `{{productFull}}` and
 * `{{company}}`, and `i18n.ts` supplies all four through `defaultVariables`, so
 * no call site passes anything.
 *
 * TODO(schema): in the finished product this page is assembled from
 * `cms_sections` rows so the admin can reorder, toggle and edit every block
 * without a deploy (Docs/07 § 1, CLAUDE.md hard rule 4). Every array in
 * `features/home/data.ts` is a section row plus its children.
 */
const HomeSections = lazy(() => import('../features/home/components/HomeSections'))

export const HomePage = () => {
  const { t } = useTranslation()

  useDocumentMeta({
    title: t('home.meta.title'),
    description: t('home.meta.description'),
    canonicalPath: routes.home,
    // Both languages live at the same URL here — the switcher is a preference,
    // not a route — so `?lang=` is the addressable form, matching index.html.
    alternates: { 'nb-NO': routes.home, en: `${routes.home}?lang=en`, 'x-default': routes.home },
  })

  return (
    <>
      <HeroSection />
      <Suspense fallback={null}>
        <HomeSections />
      </Suspense>
    </>
  )
}
